import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { priorityColor } from "@/lib/utils";
import { toast } from "sonner";
import { Stack, Plus, Trash } from "@phosphor-icons/react";

const PRIOS = ["P1", "P2", "P3", "P4"];
const emptyTiers = () => ({
  P1: { response_minutes: 15, resolution_minutes: 240 },
  P2: { response_minutes: 60, resolution_minutes: 480 },
  P3: { response_minutes: 240, resolution_minutes: 1440 },
  P4: { response_minutes: 480, resolution_minutes: 2880 },
});

export const SlaPlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({ name: "", business_hours_only: false, tiers: emptyTiers() });
  const load = () => api.get("/sla-plans").then(r => setPlans(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createPlan = async () => {
    if (!newPlan.name.trim()) return toast.error("Plan name required");
    try {
      await api.post("/sla-plans", { name: newPlan.name.trim(), business_hours_only: newPlan.business_hours_only, tiers: newPlan.tiers });
      toast.success("SLA plan created");
      setNewPlan({ name: "", business_hours_only: false, tiers: emptyTiers() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed (admin only)"); }
  };
  const updatePlan = async (id, patch) => {
    try { await api.put(`/sla-plans/${id}`, patch); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const deletePlan = async (id) => { try { await api.delete(`/sla-plans/${id}`); toast.success("Plan removed"); load(); } catch (e) { toast.error("Delete failed"); } };

  return (
    <>
      <div className="flex items-center gap-2 mb-3 mt-10">
        <Stack size={16} className="text-primary" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Reusable SLA Plans</span>
        <span className="text-[10px] font-mono text-muted-foreground">(tag to a client on the Routing Rules page)</span>
      </div>

      <Card className="p-5 border-border rounded-sm mb-4" data-testid="sla-plan-create-card">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
          <div className="lg:col-span-2">
            <Label className="text-[10px] font-mono uppercase text-muted-foreground">New plan name</Label>
            <Input data-testid="plan-name-input" className="mt-1 rounded-sm" placeholder="e.g. Gold 24×7" value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} />
          </div>
          <div className="flex items-center gap-2 h-10">
            <Switch data-testid="plan-bh-toggle" checked={newPlan.business_hours_only} onCheckedChange={(v) => setNewPlan({ ...newPlan, business_hours_only: v })} />
            <span className="text-xs text-muted-foreground">Business hours only</span>
          </div>
          <Button data-testid="plan-create-btn" onClick={createPlan} className="rounded-sm font-mono uppercase text-xs h-10"><Plus size={14} className="mr-2" />Create Plan</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {PRIOS.map(p => (
            <div key={p} className="border border-border rounded-sm p-3">
              <span className={`text-xs font-mono px-1.5 py-0.5 border ${priorityColor(p)}`}>{p}</span>
              <div className="mt-2 space-y-2">
                <div>
                  <Label className="text-[9px] font-mono uppercase text-muted-foreground">Response (min)</Label>
                  <Input type="number" data-testid={`plan-new-resp-${p}`} className="mt-0.5 rounded-sm font-mono h-8 text-xs" value={newPlan.tiers[p].response_minutes}
                    onChange={(e) => setNewPlan({ ...newPlan, tiers: { ...newPlan.tiers, [p]: { ...newPlan.tiers[p], response_minutes: parseInt(e.target.value || 0) } } })} />
                </div>
                <div>
                  <Label className="text-[9px] font-mono uppercase text-muted-foreground">Resolution (min)</Label>
                  <Input type="number" data-testid={`plan-new-res-${p}`} className="mt-0.5 rounded-sm font-mono h-8 text-xs" value={newPlan.tiers[p].resolution_minutes}
                    onChange={(e) => setNewPlan({ ...newPlan, tiers: { ...newPlan.tiers, [p]: { ...newPlan.tiers[p], resolution_minutes: parseInt(e.target.value || 0) } } })} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {plans.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="sla-plans-list">
          {plans.map(pl => (
            <Card key={pl.id} className="p-4 border-border rounded-sm" data-testid={`plan-row-${pl.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="font-display text-lg">{pl.name}</span>
                  <div className="flex items-center gap-1.5">
                    <Switch data-testid={`plan-bh-${pl.id}`} checked={pl.business_hours_only} onCheckedChange={(v) => updatePlan(pl.id, { business_hours_only: v })} />
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Business hours only</span>
                  </div>
                </div>
                <button data-testid={`plan-del-${pl.id}`} onClick={() => deletePlan(pl.id)} className="text-muted-foreground hover:text-red-700 dark:hover:text-red-400"><Trash size={15} /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PRIOS.map(p => (
                  <div key={p} className="border border-border/60 rounded-sm p-2.5">
                    <span className={`text-xs font-mono px-1.5 py-0.5 border ${priorityColor(p)}`}>{p}</span>
                    <div className="mt-2 space-y-2">
                      <div>
                        <Label className="text-[9px] font-mono uppercase text-muted-foreground">Response (min)</Label>
                        <Input type="number" data-testid={`plan-resp-${pl.id}-${p}`} className="mt-0.5 rounded-sm font-mono h-8 text-xs" defaultValue={pl.tiers?.[p]?.response_minutes ?? 0}
                          onBlur={(e) => updatePlan(pl.id, { tiers: { ...pl.tiers, [p]: { ...(pl.tiers?.[p] || {}), response_minutes: parseInt(e.target.value || 0), resolution_minutes: pl.tiers?.[p]?.resolution_minutes ?? 0 } } })} />
                      </div>
                      <div>
                        <Label className="text-[9px] font-mono uppercase text-muted-foreground">Resolution (min)</Label>
                        <Input type="number" data-testid={`plan-res-${pl.id}-${p}`} className="mt-0.5 rounded-sm font-mono h-8 text-xs" defaultValue={pl.tiers?.[p]?.resolution_minutes ?? 0}
                          onBlur={(e) => updatePlan(pl.id, { tiers: { ...pl.tiers, [p]: { ...(pl.tiers?.[p] || {}), resolution_minutes: parseInt(e.target.value || 0), response_minutes: pl.tiers?.[p]?.response_minutes ?? 0 } } })} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
